import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { ChartOptionConstants, Constants } from 'app/constants/constants';
import { Radio } from 'app/models/radio';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ExtractFormControl } from 'app/utils/validator.utils';
import {
  BoxDirection,
  BoxSize,
  MetricScaleMin,
  MetricSettings,
  ScoringStrategy,
} from '../../widget-predictive/widget-predictive.component';

interface MetricSettingsForm {
  scoring: FormControl<ScoringStrategy>;
  boxSize: FormControl<BoxSize>;
  boxDirection: FormControl<BoxDirection>;
  min: FormControl<MetricScaleMin>;
  max: FormControl<number>;
}

type MetricSettingsFormValue = ExtractFormControl<MetricSettingsForm>;

@EnableClassTelemetry()
@Component({
  selector: 'app-metric-settings-form',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './metric-settings-form.component.html',
  styleUrl: './metric-settings-form.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MetricSettingsFormComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: MetricSettingsFormComponent,
      multi: true,
    },
  ],
})
export class MetricSettingsFormComponent
  implements ControlValueAccessor, Validator, OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() header: string;

  form: FormGroup<MetricSettingsForm>;

  readonly scoringRadios: Radio<ScoringStrategy>[] = [
    { value: 'AVERAGE', label: translate('metric-settings-form.average') },
    { value: 'NPS', label: translate('metric-settings-form.nps') },
    { value: 'BOX', label: translate('metric-settings-form.box') },
  ];
  readonly boxDirectionDos = ChartOptionConstants.BOXDIRECTIONDOS;
  readonly minDos = ChartOptionConstants.MIN_SCALE_DOS;
  boxSizeDos = ChartOptionConstants.TOPBOXSIZEDOS;

  private readonly destroy$ = new Subject<void>();

  private readonly formBuilder = inject(FormBuilder);

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(settings: MetricSettings | null): void {
    if (!settings) {
      return;
    }

    const partialFormValues: Partial<MetricSettingsFormValue> = {
      scoring: settings.scoring,
      min: settings.min,
      max: settings.max,
    };

    if (settings.scoring === 'BOX') {
      partialFormValues.boxDirection = settings.boxDirection;
      partialFormValues.boxSize = settings.boxSize;
    }

    this.form.patchValue(partialFormValues);
  }

  registerOnChange(fn: any): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);
  }

  validate(): ValidationErrors | null {
    return this.form.valid ? null : { metricSettings: true };
  }

  private buildForm(): void {
    this.form = this.formBuilder.group<MetricSettingsForm>({
      scoring: new FormControl(Constants.TWTRA_DEFAULT_SCORING_STRATEGY, Validators.required),
      boxDirection: new FormControl(Constants.TWTRA_DEFAULT_BOX_DIRECTION),
      boxSize: new FormControl(Constants.TWTRA_DEFAULT_BOX_SIZE),
      min: new FormControl(Constants.TWTRA_DEFAULT_MIN_SCALE),
      max: new FormControl(Constants.TWTRA_DEFAULT_MAX_SCALE, [
        Validators.min(2),
        Validators.max(1000),
      ]),
    });

    this.form.controls.scoring.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((s) => this.handleScoringChange(s));

    this.form.controls.boxDirection.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((d) => this.handleBoxDirectionChange(d));
  }

  private handleScoringChange(scoring: ScoringStrategy): void {
    if (scoring === 'BOX') {
      this.form.controls.boxDirection.enable({ emitEvent: false });
      this.form.controls.boxSize.enable({ emitEvent: false });
    } else {
      this.form.controls.boxDirection.disable({ emitEvent: false });
      this.form.controls.boxSize.disable({ emitEvent: false });
    }
  }

  private handleBoxDirectionChange(boxDirection: BoxDirection): void {
    if (boxDirection === 'TOP') {
      this.boxSizeDos = ChartOptionConstants.TOPBOXSIZEDOS;
    } else {
      this.boxSizeDos = ChartOptionConstants.BOTTOMBOXSIZEDOS;
    }

    this.form.patchValue({ boxSize: Constants.TWTRA_DEFAULT_BOX_SIZE });
  }
}
